import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Input,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
  notification,
  Alert,
  Empty,
  Select,
  Popover,
  Divider,
} from "antd";
import {
  SafetyCertificateOutlined,
  ReloadOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import lib from "../../assets/utils/lib";
import { useAuth } from "../../assets/utils/useAuth";
import { hasAnyPermission } from "../../assets/utils/permissions";

const { Text, Paragraph } = Typography;
const { Option } = Select;

export const ENCOUNTER_CODE_MANAGE_PERMS = [
  "visit_codes.manage_all",
  "admin.*",
  "checkins.create",
];

const LABEL_MAX = 100;
const ERR_PENDING_EXISTS = "ENCOUNTER_CODE_PENDING_EXISTS";
const HIGHER_TIER_WARNING = "This is a higher tier facility, proceed.";
const TERMINAL_STATUSES = new Set([
  "used",
  "redeemed",
  "cancelled",
  "canceled",
  "expired",
]);

function formatExpiry(expiresAt) {
  if (!expiresAt) return "—";
  try {
    const d = new Date(String(expiresAt).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return String(expiresAt);
    return d.toLocaleString();
  } catch {
    return String(expiresAt);
  }
}

function expiryHint(expiresAt) {
  if (!expiresAt) return null;
  try {
    const d = new Date(String(expiresAt).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return null;
    const ms = d.getTime() - Date.now();
    if (ms <= 0) return "Expired";
    const hours = Math.floor(ms / 3600000);
    const mins = Math.floor((ms % 3600000) / 60000);
    if (hours >= 1) return `${hours}h ${mins}m left`;
    return `${mins}m left`;
  } catch {
    return null;
  }
}

function statusColor(status) {
  switch (String(status || "").toLowerCase()) {
    case "pending":
    case "active":
      return "blue";
    case "used":
    case "redeemed":
      return "green";
    case "cancelled":
    case "canceled":
      return "default";
    case "expired":
      return "orange";
    default:
      return "default";
  }
}

function statusLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "pending";
  if (s === "redeemed") return "used";
  return s || "—";
}

function rowLabel(row) {
  return (
    row?.display_label ||
    row?.label ||
    row?.facility_name ||
    (row?.facility_id != null ? `Facility #${row.facility_id}` : null)
  );
}

function customLabel(row) {
  const label = String(row?.label || "").trim();
  return label || null;
}

function isMachineErrorCode(code) {
  return /^ENCOUNTER_CODE_/i.test(String(code || "").trim());
}

/**
 * Normalize generate / list / pending-exists payloads to a single present row.
 * Admin generate success returns the present object at `data`.
 * List / PENDING_EXISTS return `data.active`.
 * Never treats API machine codes (ENCOUNTER_CODE_*) as visit tokens.
 */
function normalizeEncounterCode(payload) {
  if (!payload || typeof payload !== "object") return null;

  const row =
    payload.active && typeof payload.active === "object"
      ? payload.active
      : payload.visit_code && typeof payload.visit_code === "object"
        ? payload.visit_code
        : typeof payload.code === "string" || typeof payload.code === "number"
          ? payload
          : null;

  if (!row || typeof row !== "object") return null;

  const code = String(row.code ?? row.visit_code ?? "").trim();
  if (!code || isMachineErrorCode(code)) return null;

  return { ...row, code };
}

/** Dig active code out of success, list, or error bodies (incl. nested data.active). */
export function extractActiveEncounterCode(payload) {
  if (!payload || typeof payload !== "object") return null;
  return (
    normalizeEncounterCode(payload?.data?.active) ||
    normalizeEncounterCode(payload?.active) ||
    normalizeEncounterCode(payload?.data) ||
    normalizeEncounterCode(payload)
  );
}

function isRedeemableActive(row) {
  if (!row) return false;
  const code = String(row.code || "").trim();
  if (!code || isMachineErrorCode(code)) return false;
  const status = String(row.status || row.raw_status || "").toLowerCase();
  // Missing status: still show (PENDING_EXISTS payloads always include status, but be defensive).
  if (!status) return true;
  if (TERMINAL_STATUSES.has(status)) return false;
  return true;
}

function isEnrolleeActive(enrolleeData) {
  if (!enrolleeData || typeof enrolleeData !== "object") return true;
  if (enrolleeData.status == null && enrolleeData.enrollee_status == null) {
    return true;
  }
  const raw =
    enrolleeData.status != null ? enrolleeData.status : enrolleeData.enrollee_status;
  return Number(raw) === 1;
}

export function canManageEncounterCodes(permissions) {
  return hasAnyPermission(ENCOUNTER_CODE_MANAGE_PERMS, permissions || {});
}

function copyText(code) {
  if (!code) return;
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(code).then(
      () => notification.success({ message: "Copied", placement: "topLeft", duration: 1.5 }),
      () => fallbackCopy(code),
    );
    return;
  }
  fallbackCopy(code);
}

function fallbackCopy(code) {
  try {
    const el = document.createElement("textarea");
    el.value = code;
    el.setAttribute("readonly", "");
    el.style.position = "absolute";
    el.style.left = "-9999px";
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    notification.success({ message: "Copied", placement: "topLeft", duration: 1.5 });
  } catch {
    notification.error({ message: "Copy failed", placement: "topLeft" });
  }
}

/**
 * Compact View / Generate actions for enrollee search cards.
 * View peeks active code via admin list endpoint; Generate opens the full panel.
 */
export function EnrolleeEncounterCodeQuickActions({ record, onOpenPanel }) {
  const [peekOpen, setPeekOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const enrolleeId = record?.id || record?.user_id;

  const fetchActive = useCallback(async () => {
    if (!enrolleeId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const resp = await lib.listEnrolleeEncounterCodes(enrolleeId, { limit: 5 });
      const body = resp?.data;
      if (Number(body?.error) !== 0 && body?.error !== false && body?.error != null) {
        setLoadError(body?.message || "Could not load Check-in code");
        setActive(null);
        return;
      }
      const row = extractActiveEncounterCode(body?.data) || extractActiveEncounterCode(body);
      setActive(isRedeemableActive(row) ? row : null);
    } catch (e) {
      setLoadError(
        e?.response?.data?.message || e?.message || "Could not load Check-in code",
      );
      setActive(null);
    } finally {
      setLoading(false);
    }
  }, [enrolleeId]);

  const code = String(active?.code || "").trim();
  const pending = isRedeemableActive(active);

  const peekContent = (
    <div style={{ maxWidth: 280 }} onClick={(e) => e.stopPropagation()}>
      {loading ? (
        <Spin size="small" />
      ) : loadError ? (
        <Text type="danger" style={{ fontSize: 12 }}>{loadError}</Text>
      ) : pending ? (
        <Space direction="vertical" size={6} style={{ width: "100%" }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Active Check-in code</Text>
          <Text code style={{ fontSize: 16, fontWeight: 700 }}>{code}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {active.facility_name || active.facility?.name || (active.facility_id != null ? `Facility #${active.facility_id}` : "—")}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Expires: {formatExpiry(active.expires_at)}
          </Text>
          <Space size={4} wrap>
            <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyText(code)}>
              Copy
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => {
                setPeekOpen(false);
                onOpenPanel?.();
              }}
            >
              Manage
            </Button>
          </Space>
        </Space>
      ) : (
        <Space direction="vertical" size={6}>
          <Text type="secondary" style={{ fontSize: 12 }}>No pending Check-in code</Text>
          <Button
            type="link"
            size="small"
            icon={<SafetyCertificateOutlined />}
            onClick={() => {
              setPeekOpen(false);
              onOpenPanel?.();
            }}
          >
            Generate one
          </Button>
        </Space>
      )}
    </div>
  );

  return (
    <>
      <Popover
        trigger="click"
        open={peekOpen}
        onOpenChange={(next) => {
          setPeekOpen(next);
          if (next) fetchActive();
        }}
        content={peekContent}
        placement="bottomRight"
      >
        <Button
          type="link"
          size="small"
          className="listing-card__action"
          icon={<SafetyCertificateOutlined />}
          onClick={(e) => e.stopPropagation()}
        >
          View code
        </Button>
      </Popover>
      <Button
        type="link"
        size="small"
        className="listing-card__action"
        icon={<SafetyCertificateOutlined />}
        onClick={(e) => {
          e.stopPropagation();
          onOpenPanel?.();
        }}
      >
        Generate
      </Button>
    </>
  );
}

/**
 * CS tooling on enrollee profile: generate / view active / cancel Encounter Codes.
 * Facility only — no checkin type. Hidden when API feature is off (404).
 */
export function EnrolleeEncounterCodes({
  enrolleeData,
  modalZIndex,
  autoOpen = false,
  hideTrigger = false,
  onPanelClose,
}) {
  const { permissions } = useAuth();
  const canManage = canManageEncounterCodes(permissions);

  const [open, setOpen] = useState(Boolean(autoOpen));
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [featureOff, setFeatureOff] = useState(false);
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [facilityId, setFacilityId] = useState(null);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [facilitySearchLoading, setFacilitySearchLoading] = useState(false);
  const [label, setLabel] = useState("");
  const [pendingExistsNotice, setPendingExistsNotice] = useState(false);

  const loadSeq = useRef(0);

  const enrolleeId = enrolleeData?.id || enrolleeData?.user_id;
  const insuranceNo = enrolleeData?.insurance_no;
  const enrolleeName = [enrolleeData?.first_name, enrolleeData?.surname]
    .filter(Boolean)
    .join(" ");
  const enrolleeActive = isEnrolleeActive(enrolleeData);
  const selectedFacility = facilityOptions.find(
    (f) => Number(f.id) === Number(facilityId),
  );
  const selectedIsHigherTier = Boolean(selectedFacility?.is_higher_tier);

  const closePanel = useCallback(() => {
    setOpen(false);
    setPendingExistsNotice(false);
    onPanelClose?.();
  }, [onPanelClose]);

  const load = useCallback(async () => {
    if (!enrolleeId) return;
    const seq = ++loadSeq.current;
    setLoading(true);
    setFeatureOff(false);
    try {
      const resp = await lib.listEnrolleeEncounterCodes(enrolleeId, { limit: 20 });
      if (seq !== loadSeq.current) return;
      const body = resp?.data;
      if (Number(body?.error) !== 0 && body?.error !== false && body?.error != null) {
        notification.error({
          message: body?.message || "Could not load Check-in codes",
          placement: "topLeft",
        });
        return;
      }
      const nextActive =
        extractActiveEncounterCode(body?.data) ||
        extractActiveEncounterCode(body);
      setActive(nextActive);
      setHistory(Array.isArray(body?.data?.history) ? body.data.history : []);
      if (isRedeemableActive(nextActive)) {
        setPendingExistsNotice(false);
      }
    } catch (e) {
      if (seq !== loadSeq.current) return;
      const status = Number(e?.response?.status || 0);
      if (status === 404) {
        setFeatureOff(true);
        setActive(null);
        setHistory([]);
        return;
      }
      notification.error({
        message:
          e?.response?.data?.message ||
          e?.message ||
          "Could not load Check-in codes",
        placement: "topLeft",
      });
    } finally {
      if (seq === loadSeq.current) {
        setLoading(false);
      }
    }
  }, [enrolleeId]);

  // Reset when switching enrollee so we never show another member's code.
  useEffect(() => {
    loadSeq.current += 1;
    setActive(null);
    setHistory([]);
    setFacilityId(null);
    setFacilityOptions([]);
    setLabel("");
    setPendingExistsNotice(false);
    setFeatureOff(false);
  }, [enrolleeId]);

  useEffect(() => {
    if (autoOpen) {
      setOpen(true);
    }
  }, [autoOpen]);

  // Always fetch active + history when the panel opens or enrollee changes while open.
  useEffect(() => {
    if (open && enrolleeId) {
      load();
    }
  }, [open, enrolleeId, load]);

  const searchFacilities = useCallback(
    async (query) => {
      const q = String(query || "").trim();
      if (q.length < 2) {
        setFacilityOptions([]);
        return;
      }
      if (!enrolleeId && !insuranceNo) {
        setFacilityOptions([]);
        return;
      }
      setFacilitySearchLoading(true);
      try {
        // Plan/tier scoped only — the enrollee's location never narrows this list.
        const response = await lib.getEncounterCodeFacilities({
          user_id: enrolleeId,
          insurance_no: insuranceNo,
          query: q,
          limit: 20,
        });
        const data = response?.data;
        const list = Array.isArray(data?.data) ? data.data : [];
        setFacilityOptions(
          list
            .map((f) => ({
              id: f.facility_id ?? f.id,
              name: f.facility_name || f.name,
              is_higher_tier: Boolean(f.is_higher_tier),
              user_tier: f.user_tier,
              fac_tier: f.fac_tier,
            }))
            .filter((f) => f.id != null && f.name),
        );
      } catch {
        setFacilityOptions([]);
      } finally {
        setFacilitySearchLoading(false);
      }
    },
    [enrolleeId, insuranceNo],
  );

  if (!canManage || !enrolleeId) {
    return null;
  }

  const hydrateFromPendingExists = (payload, message) => {
    const existing = extractActiveEncounterCode(payload);
    if (!existing) return false;
    // Invalidate in-flight list calls so they cannot wipe this hydration.
    loadSeq.current += 1;
    setLoading(false);
    setActive(existing);
    setPendingExistsNotice(true);
    notification.warning({
      message: "Active Check-in code already exists",
      description:
        message ||
        "This enrollee already has an active code. Cancel it or wait until it is used or expires before issuing another.",
      placement: "topLeft",
      duration: 8,
    });
    return true;
  };

  const handleGenerate = async () => {
    if (!enrolleeActive) {
      notification.error({
        message: "Enrollee is inactive and cannot be issued a Check-in code",
        placement: "topLeft",
      });
      return;
    }
    if (!facilityId) {
      notification.warning({
        message: "Select a facility first",
        placement: "topLeft",
      });
      return;
    }
    const trimmedLabel = String(label || "").trim();
    if (trimmedLabel.length > LABEL_MAX) {
      notification.warning({
        message: `Label must be ${LABEL_MAX} characters or fewer`,
        placement: "topLeft",
      });
      return;
    }
    setGenerating(true);
    setPendingExistsNotice(false);
    try {
      const payload = {
        facility_id: facilityId,
        user_id: enrolleeId,
        insurance_no: insuranceNo,
      };
      if (trimmedLabel) {
        payload.label = trimmedLabel;
      }
      const resp = await lib.generateAdminEncounterCode(payload);
      const body = resp?.data;
      if (Number(body?.error) !== 0) {
        if (
          body?.code === ERR_PENDING_EXISTS &&
          hydrateFromPendingExists(body, body?.message)
        ) {
          return;
        }
        const existing = extractActiveEncounterCode(body);
        if (existing) {
          // Defensive: some gateways may flatten 409 bodies onto 200 + error flag.
          hydrateFromPendingExists(body, body?.message);
          return;
        }
        notification.error({
          message: body?.message || "Failed to generate Check-in code",
          placement: "topLeft",
        });
        return;
      }
      const issued = extractActiveEncounterCode(body?.data) || extractActiveEncounterCode(body);
      const higherTierWarning =
        body?.warning ||
        body?.data?.warning ||
        (body?.data?.is_higher_tier || issued?.is_higher_tier
          ? HIGHER_TIER_WARNING
          : null);
      notification.success({
        message: body?.message || "Check-in code issued",
        description: issued?.code ? `Code: ${issued.code}` : undefined,
        placement: "topLeft",
        duration: 6,
      });
      if (higherTierWarning) {
        notification.warning({
          message: higherTierWarning,
          placement: "topLeft",
          duration: 8,
        });
      }
      if (issued) {
        setActive(issued);
      }
      setFacilityId(null);
      setFacilityOptions([]);
      setLabel("");
      try {
        await load();
      } catch {
        /* load already notifies */
      }
      if (issued) {
        setActive((prev) => (isRedeemableActive(prev) ? prev : issued));
      }
    } catch (e) {
      const status = Number(e?.response?.status || 0);
      const errBody = e?.response?.data;
      if (status === 404) {
        setFeatureOff(true);
        notification.info({
          message: "Check-in codes are not enabled on this environment",
          placement: "topLeft",
        });
        return;
      }
      const isPendingExists =
        errBody?.code === ERR_PENDING_EXISTS ||
        (status === 409 && Boolean(extractActiveEncounterCode(errBody)));
      if (isPendingExists && hydrateFromPendingExists(errBody, errBody?.message)) {
        return;
      }
      notification.error({
        message:
          errBody?.message ||
          e?.message ||
          "Failed to generate Check-in code",
        placement: "topLeft",
      });
    } finally {
      setGenerating(false);
    }
  };

  const confirmCancel = (code) => {
    if (!code) return;
    Modal.confirm({
      title: "Cancel this Check-in code?",
      content: "You can generate a new one afterwards. This cannot be undone.",
      okText: "Cancel code",
      okButtonProps: { danger: true },
      cancelText: "Keep code",
      zIndex: modalZIndex != null ? modalZIndex + 4 : undefined,
      onOk: () => handleCancel(code),
    });
  };

  const handleCancel = async (code) => {
    if (!code) return;
    setCancelling(true);
    try {
      const resp = await lib.cancelAdminEncounterCode(code);
      const body = resp?.data;
      if (Number(body?.error) !== 0) {
        notification.error({
          message: body?.message || "Could not cancel Check-in code",
          placement: "topLeft",
        });
        return;
      }
      notification.success({
        message: body?.message || "Check-in code cancelled",
        placement: "topLeft",
      });
      setPendingExistsNotice(false);
      await load();
    } catch (e) {
      notification.error({
        message:
          e?.response?.data?.message ||
          e?.message ||
          "Could not cancel Check-in code",
        placement: "topLeft",
      });
    } finally {
      setCancelling(false);
    }
  };

  const isPendingActive = isRedeemableActive(active);
  const activeCode = String(active?.code || "").trim();
  const activeExpiryHint = isPendingActive ? expiryHint(active?.expires_at) : null;

  return (
    <>
      {!hideTrigger ? (
        <Button
          icon={<SafetyCertificateOutlined />}
          onClick={() => setOpen(true)}
        >
          Check-in codes
        </Button>
      ) : null}
      <Modal
        title={
          <Space direction="vertical" size={0}>
            <Space>
              <SafetyCertificateOutlined />
              Check-in codes
            </Space>
            <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
              {[enrolleeName, insuranceNo].filter(Boolean).join(" · ") || "Enrollee"}
            </Text>
          </Space>
        }
        open={open}
        onCancel={closePanel}
        footer={[
          <Button key="close" onClick={closePanel}>
            Close
          </Button>,
        ]}
        width={640}
        destroyOnClose
        zIndex={modalZIndex != null ? modalZIndex + 2 : undefined}
      >
        {featureOff ? (
          <Alert
            type="info"
            showIcon
            message="Check-in codes are not enabled on this API environment."
          />
        ) : (
          <Spin spinning={loading}>
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Alert
                type="info"
                showIcon
                message="For walk-in visits"
                description="Pick the hospital the member will visit. Staff choose the clinic type at the desk when they redeem the code."
              />

              {!enrolleeActive ? (
                <Alert
                  type="warning"
                  showIcon
                  message="Enrollee is inactive"
                  description="Check-in codes cannot be generated for inactive enrollees. Reactivate the member first."
                />
              ) : null}

              {pendingExistsNotice && isPendingActive ? (
                <Alert
                  type="warning"
                  showIcon
                  message="Already have an active Check-in code"
                  description="Cancel the code below or wait until it is used or expires before issuing another."
                />
              ) : null}

              <div>
                <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Text strong>Active code</Text>
                  <Button
                    type="text"
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={load}
                    loading={loading}
                  >
                    Refresh
                  </Button>
                </div>
                {isPendingActive ? (
                  <Alert
                    type="success"
                    showIcon
                    message={
                      <Space direction="vertical" size={6} style={{ width: "100%" }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Check-in code
                        </Text>
                        <Space wrap align="center">
                          <Text
                            code
                            style={{
                              fontSize: 22,
                              letterSpacing: 1,
                              fontWeight: 700,
                              padding: "4px 8px",
                            }}
                            copyable={{ text: activeCode }}
                          >
                            {activeCode}
                          </Text>
                          <Tag color="blue">{statusLabel(active.status || active.raw_status)}</Tag>
                          <Button
                            type="link"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => copyText(activeCode)}
                          >
                            Copy
                          </Button>
                        </Space>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={4}>
                        {customLabel(active) ? (
                          <Text type="secondary">
                            Label: {customLabel(active)}
                          </Text>
                        ) : null}
                        <Text type="secondary">
                          Facility:{" "}
                          {active.facility_name ||
                            active.facility?.name ||
                            active.facility_id ||
                            "—"}
                        </Text>
                        <Text type="secondary">
                          Expires: {formatExpiry(active.expires_at)}
                          {activeExpiryHint ? ` · ${activeExpiryHint}` : ""}
                        </Text>
                        <Paragraph type="secondary" style={{ marginBottom: 8, fontSize: 12 }}>
                          Read this code to the member, or copy and send it. It only works at the facility above.
                        </Paragraph>
                        <Button
                          danger
                          size="small"
                          loading={cancelling}
                          onClick={() => confirmCancel(activeCode)}
                        >
                          Cancel code
                        </Button>
                      </Space>
                    }
                  />
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No pending Check-in code"
                  />
                )}
              </div>

              {!isPendingActive && enrolleeActive ? (
                <div>
                  <Text strong style={{ display: "block", marginBottom: 8 }}>
                    Generate Check-in code
                  </Text>
                  <Paragraph type="secondary" style={{ marginBottom: 8, fontSize: 13 }}>
                    Search for the facility, optionally add a short label for your notes, then generate.
                  </Paragraph>
                  <Space direction="vertical" style={{ width: "100%" }} size={10}>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                        Facility
                      </Text>
                      <Select
                        showSearch
                        allowClear
                        filterOption={false}
                        placeholder="Type at least 2 characters to search facilities"
                        value={facilityId}
                        onSearch={searchFacilities}
                        onChange={(id) => setFacilityId(id ?? null)}
                        notFoundContent={facilitySearchLoading ? <Spin size="small" /> : null}
                        style={{ width: "100%" }}
                      >
                        {facilityOptions.map((f) => (
                          <Option key={f.id} value={Number(f.id)}>
                            {f.name}
                            {f.is_higher_tier ? " (higher tier)" : ""}
                          </Option>
                        ))}
                      </Select>
                      {selectedIsHigherTier ? (
                        <Alert
                          type="warning"
                          showIcon
                          message={HIGHER_TIER_WARNING}
                          style={{ marginTop: 8 }}
                        />
                      ) : null}
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                        Label (optional)
                      </Text>
                      <Input
                        allowClear
                        maxLength={LABEL_MAX}
                        showCount
                        placeholder="e.g. Mom — LUTH walk-in"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                      />
                    </div>
                    <Button
                      type="primary"
                      loading={generating}
                      disabled={!facilityId}
                      onClick={handleGenerate}
                      icon={<SafetyCertificateOutlined />}
                    >
                      Generate Check-in code
                    </Button>
                  </Space>
                </div>
              ) : null}

              {history.length > 0 ? (
                <div>
                  <Divider style={{ margin: "8px 0" }} />
                  <Text strong style={{ display: "block", marginBottom: 8 }}>
                    Recent history
                  </Text>
                  <Space direction="vertical" style={{ width: "100%" }} size={6}>
                    {history.slice(0, 8).map((row) => {
                      const st = statusLabel(row.status);
                      return (
                        <div
                          key={`${row.code}-${row.status}-${row.issued_at || row.created_at}`}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 8,
                            alignItems: "flex-start",
                            padding: "8px 0",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          <Space direction="vertical" size={2} style={{ flex: 1, minWidth: 0 }}>
                            <Space wrap size={6}>
                              <Text code style={{ fontSize: 12 }}>
                                {row.code}
                              </Text>
                              <Tag color={statusColor(st)}>{st}</Tag>
                            </Space>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {rowLabel(row)}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Expires: {formatExpiry(row.expires_at)}
                            </Text>
                          </Space>
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => copyText(row.code)}
                            title="Copy code"
                          />
                        </div>
                      );
                    })}
                  </Space>
                </div>
              ) : null}
            </Space>
          </Spin>
        )}
      </Modal>
    </>
  );
}

export default EnrolleeEncounterCodes;
